import { Search, Filter, Loader2 } from "lucide-react";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { useEventFiltersStore } from "../../stores/event-filters-store";
import { useDebounce } from "../../lib/hooks/useDebounce";
import type { DateFilterOption } from "../../lib/date-utils";

interface EventFiltersProps {
  availableTags: Array<{ name: string }>;
}

export const EventFilters = ({ availableTags }: EventFiltersProps) => {
  const {
    search,
    selectedTags,
    dateFilter,
    setSearch,
    setSelectedTags,
    setDateFilter,
    clearFilters,
  } = useEventFiltersStore();

  // Debounce search to show loading state
  const debouncedSearch = useDebounce(search, 300);
  const isSearching = search !== debouncedSearch;

  const handleTagToggle = (tagName: string) => {
    if (selectedTags.includes(tagName)) {
      setSelectedTags(selectedTags.filter((name) => name !== tagName));
    } else {
      setSelectedTags([...selectedTags, tagName]);
    }
  };

  const getTagVariant = () => {
    // Always use default variant for consistent styling
    return "default" as const;
  };

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Filter className="w-5 h-5 mr-2" />
          Filter Events
        </h3>
        <Button variant="outline" size="sm" onClick={clearFilters}>
          Clear All
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Input
          label="Search events"
          placeholder="Search by title or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
        <div className="absolute left-3 top-[2rem] pointer-events-none">
          {isSearching ? (
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
          ) : (
            <Search className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Tags
        </label>
        <div className="flex flex-wrap gap-2">
          {availableTags.map((tag) => {
            const isActive = selectedTags.includes(tag.name);
            return (
              <Badge
                key={tag.name}
                variant={isActive ? getTagVariant() : "default"}
                className={`cursor-pointer hover:opacity-80 transition-all ${
                  isActive ? "ring-2 ring-primary-500 shadow-md" : ""
                }`}
                onClick={() => handleTagToggle(tag.name)}
                title={
                  isActive
                    ? `Filtering by ${tag.name} - click to remove`
                    : `Filter by ${tag.name}`
                }
              >
                {tag.name}
                {isActive && " ✓"}
              </Badge>
            );
          })}
        </div>
      </div>

      {/* Date Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Date
        </label>
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value as DateFilterOption)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="all">All dates</option>
          <option value="this-week">This week</option>
          <option value="this-month">This month</option>
          <option value="next-month">Next month</option>
        </select>
      </div>
    </div>
  );
};
